import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import BaseModal from './common/BaseModal';
import { useFormattedNumber } from '../hooks/useFormattedNumber';

// Define the parameters that can be optimized
const GOAL_SEEK_PARAMS = {
  PRICE: 'price',
  DOWN_PAYMENT: 'downPayment',
  INTEREST_RATE: 'interestRate',
  CLOSING_COSTS: 'closingCosts',
  MONTHLY_RENT: 'monthlyRent'
};

// Define the metrics that can be optimized for
const GOAL_SEEK_METRICS = {
  TOTAL_ROI: 'totalROI',
  CASH_ON_CASH: 'cashOnCash',
  CAP_RATE: 'capRate'
};

const GoalSeekModal = ({ show, onHide, scenarioData, onApplyPrice }) => {
  const [targetValue, setTargetValue] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [selectedParam, setSelectedParam] = useState(GOAL_SEEK_PARAMS.PRICE);
  const [selectedMetric, setSelectedMetric] = useState(GOAL_SEEK_METRICS.TOTAL_ROI);

  const resultValueFormat = useFormattedNumber('');

  useEffect(() => {
    if (show) {
      // Reset state when modal opens
      setTargetValue('');
      setResult(null);
      setError('');
      setIsCalculating(false);
      setSelectedParam(GOAL_SEEK_PARAMS.PRICE);
      setSelectedMetric(GOAL_SEEK_METRICS.TOTAL_ROI);
    }
  }, [show]);

  const handleTargetValueChange = (e) => {
    const value = e.target.value.replace(/%/g, '');
    setTargetValue(value);
    setResult(null);
    setError('');
  };

  const calculateOptimalValue = () => {
    setIsCalculating(true);
    setError('');
    setResult(null);

    try {
      const targetMetricValue = parseFloat(targetValue);
      
      if (isNaN(targetMetricValue) || targetMetricValue <= 0) {
        setError(`Please enter a valid positive ${selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 'ROI' : selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 'Cash on Cash' : 'Cap Rate'} target.`);
        setIsCalculating(false);
        return;
      }

      // Binary search to find the optimal value for the selected parameter
      // that achieves the target ROI
      const findOptimalValue = () => {
        let low, high, currentValue, paramName, paramUnit, inverse;
        
        switch (selectedParam) {
          case GOAL_SEEK_PARAMS.PRICE:
            low = 1000; // Minimum reasonable price
            high = 10000000; // Maximum reasonable price
            inverse = true; // Higher price = lower ROI
            paramName = 'price';
            paramUnit = '$';
            break;
            
          case GOAL_SEEK_PARAMS.DOWN_PAYMENT:
            low = 1; // Minimum down payment %
            high = 100; // Maximum down payment %
            inverse = false; // Higher down payment = higher ROI
            paramName = 'downPaymentPercent';
            paramUnit = '%';
            break;
            
          case GOAL_SEEK_PARAMS.INTEREST_RATE:
            low = 0.1; // Minimum interest rate
            high = 20; // Maximum interest rate
            inverse = true; // Higher interest rate = lower ROI
            paramName = 'interestRate';
            paramUnit = '%';
            break;
            
          case GOAL_SEEK_PARAMS.CLOSING_COSTS:
            low = 0; // Minimum closing costs
            high = 100000; // Maximum closing costs
            inverse = true; // Higher closing costs = lower ROI
            paramName = 'closingCosts';
            paramUnit = '$';
            break;
            
          case GOAL_SEEK_PARAMS.MONTHLY_RENT:
            low = 100; // Minimum monthly rent
            high = 50000; // Maximum monthly rent
            inverse = false; // Higher rent = higher ROI
            paramName = 'monthlyRent';
            paramUnit = '$';
            break;
          
          default:
            throw new Error('Invalid parameter selected');
        }
        
        let bestValue = null;
        let bestROI = null;
        let iterations = 0;
        const maxIterations = 20; // Prevent infinite loops
        
        while (iterations < maxIterations && high - low > (paramName === 'interestRate' || paramName === 'downPaymentPercent' ? 0.01 : 1)) {
          iterations++;
          const midValue = (low + high) / 2;
          const calculatedMetric = calculateMetricWithParam(paramName, midValue);
          
          // If we found a value that gives exactly the target metric, return it
          if (Math.abs(calculatedMetric - targetMetricValue) < 0.01) {
            bestValue = midValue;
            bestROI = calculatedMetric;
            break;
          }
          
          // Adjust search range based on calculated metric and relationship with target
          if (calculatedMetric < targetMetricValue) {
            // If calculated is less than target:
            // - For inverse parameters (price, interest rate, closing costs): we need a LOWER value to get HIGHER returns
            // - For direct parameters (down payment, monthly rent): we need a HIGHER value to get HIGHER returns
            if (inverse) {
              high = midValue; // For inverse: lower the max value
            } else {
              low = midValue; // For direct: raise the min value
            }
          } else {
            // If calculated is greater than target:
            // - For inverse parameters: we need a HIGHER value to get LOWER returns
            // - For direct parameters: we need a LOWER value to get LOWER returns
            if (inverse) {
              low = midValue; // For inverse: raise the min value
            } else {
              high = midValue; // For direct: lower the max value
            }
          }
          
          // Always save the closest value we've found so far
          if (bestROI === null || Math.abs(calculatedMetric - targetMetricValue) < Math.abs(bestROI - targetMetricValue)) {
            bestValue = midValue;
            bestROI = calculatedMetric;
          }
        }
        
        return { value: bestValue, roi: bestROI, paramName, paramUnit };
      };
      
      const result = findOptimalValue();
      
      if (result.value === null) {
        setError(`Could not find a suitable ${result.paramName} for the target ROI. Try a different target.`);
      } else {
        // Round all values to 2 decimal places for consistency
        const roundedValue = Math.round(result.value * 100) / 100;
          
        setResult({
          paramName: result.paramName,
          paramUnit: result.paramUnit,
          optimalValue: roundedValue,
          calculatedROI: result.roi
        });
        resultValueFormat.onChange({ target: { value: roundedValue.toString() } });
      }
    } catch (error) {
      console.error('Error in goal seek calculation:', error);
      setError('An error occurred during calculation. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Function to calculate the selected metric with a specific parameter value
  const calculateMetricWithParam = (paramName, paramValue) => {
    // Extract all necessary values from scenarioData
    // Override the selected parameter with the provided value
    // Round price and closing costs to 2 decimal places
    const price = paramName === 'price' 
      ? Math.round(paramValue * 100) / 100 
      : Number(scenarioData.price) || 100000;
    const downPaymentPercent = paramName === 'downPaymentPercent' 
      ? paramValue 
      : Number(scenarioData.downPaymentPercent) || 20;
    const interestRate = paramName === 'interestRate' 
      ? paramValue 
      : Number(scenarioData.interestRate) || 4;
    const term = Number(scenarioData.loanTermYears) || 30;
    const monthlyRent = paramName === 'monthlyRent' 
      ? paramValue 
      : Number(scenarioData.monthlyRent) || 0;
    const closingCosts = paramName === 'closingCosts' 
      ? Math.round(paramValue * 100) / 100 
      : Number(scenarioData.closingCosts) || 0;
    const repairEstimates = Number(scenarioData.repairEstimates) || 0;
    const monthlyInsurance = Number(scenarioData.monthlyInsurance) || 0;
    const propertyTax = Number(scenarioData.propertyTax) || 0;
    const managementFee = Number(scenarioData.managementFee) || 0;
    const vacancyRate = Number(scenarioData.vacancyRate) || 0;
    const maintenanceRate = Number(scenarioData.maintenanceRate) || 0;
    const appreciationRate = Number(scenarioData.appreciation) || 0;
    
    // Important: Down payment is always a percentage of the purchase price
    // This means the actual down payment amount changes when price changes

    // Calculate mortgage payment
    const principal = price * (1 - downPaymentPercent / 100);
    const monthlyRate = (interestRate / 100) / 12;
    const numberOfPayments = term * 12;
    
    const monthlyMortgage = principal * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    // Calculate yearly values
    const yearlyGrossRent = monthlyRent * 12;
    const yearlyMortgage = monthlyMortgage * 12;
    const yearlyInsurance = monthlyInsurance * 12;
    const yearlyTaxes = propertyTax;
    const yearlyManagement = yearlyGrossRent * (managementFee / 100);
    const yearlyVacancy = yearlyGrossRent * (vacancyRate / 100);
    const yearlyMaintenance = yearlyGrossRent * (maintenanceRate / 100);
    
    const totalExpenses = yearlyInsurance + yearlyTaxes + yearlyManagement + yearlyVacancy + yearlyMaintenance;
    const noi = yearlyGrossRent - totalExpenses;
    const cashFlow = noi - yearlyMortgage;
    
    // Calculate total cash invested - down payment is always a percentage of purchase price
    const downPaymentAmount = price * (downPaymentPercent / 100);
    const totalCashInvested = downPaymentAmount + closingCosts + repairEstimates;
    const cashFlowPercent = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;
    
    const initialValue = Number(scenarioData.initialHomeValue) || price;
    const currentYearValue = initialValue * (1 + appreciationRate / 100);
    const yearlyAppreciationAmount = currentYearValue - initialValue;
    const appreciationPercent = totalCashInvested > 0 ? (yearlyAppreciationAmount / totalCashInvested) * 100 : 0;
    
    // Calculate principal paydown for first year
    let remainingBalance = price * (1 - downPaymentPercent / 100);
    const startOfYearBalance = remainingBalance;
    
    // Calculate first year's principal paydown
    for (let month = 1; month <= 12; month++) {
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = monthlyMortgage - interestPayment;
      remainingBalance = remainingBalance - principalPayment;
    }
    
    const yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
    const principalPaydownPercent = totalCashInvested > 0 ? (yearlyPrincipalPaydown / totalCashInvested) * 100 : 0;
    
    // Calculate metrics
    const totalROI = appreciationPercent + cashFlowPercent + principalPaydownPercent;
    const cashOnCashReturn = cashFlowPercent;
    const capRate = noi / price * 100;
    
    // Return the selected metric
    switch (selectedMetric) {
      case GOAL_SEEK_METRICS.TOTAL_ROI:
        return totalROI;
      case GOAL_SEEK_METRICS.CASH_ON_CASH:
        return cashOnCashReturn;
      case GOAL_SEEK_METRICS.CAP_RATE:
        return capRate;
      default:
        return totalROI;
    }
  };

  const handleApplyResult = () => {
    if (result && result.optimalValue) {
      switch (result.paramName) {
        case 'price':
          onApplyPrice(result.optimalValue);
          break;
        case 'downPaymentPercent':
          onApplyPrice(undefined, result.optimalValue);
          break;
        case 'interestRate':
          onApplyPrice(undefined, undefined, result.optimalValue);
          break;
        case 'closingCosts':
          onApplyPrice(undefined, undefined, undefined, result.optimalValue);
          break;
        case 'monthlyRent':
          onApplyPrice(undefined, undefined, undefined, undefined, result.optimalValue);
          break;
      }
      onHide();
    }
  };

  return (
    <BaseModal
      isOpen={show}
      onClose={onHide}
      title="Goal Seek"
      maxWidth="900px"
      width="70%"
      footer={
        <>
          <button 
            className="btn btn-outline-secondary me-2" 
            onClick={onHide}
          >
            Cancel
          </button>
          {result ? (
            <button 
              className="btn btn-primary" 
              onClick={handleApplyResult}
            >
              Apply Value
            </button>
          ) : (
            <button 
              className="btn btn-primary" 
              onClick={calculateOptimalValue}
              disabled={isCalculating || !targetValue}
            >
              {isCalculating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Calculating...
                </>
              ) : 'Calculate'}
            </button>
          )}
        </>
      }
    >
      <div className="p-3">
        <p className="text-white mb-3">
          Find the optimal value for a parameter that can achieve your target {selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 'Total ROI' : 
            selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 'Cash on Cash Return' : 
            'Cap Rate'}.
          This will adjust your selected parameter while keeping all other parameters constant.
        </p>
        {selectedParam === GOAL_SEEK_PARAMS.PRICE && (
          <p className="small text-muted mb-3">
            <i className="bi bi-info-circle me-1"></i>
            Note: Down payment is always calculated as a percentage of the purchase price.
            When optimizing purchase price, the down payment amount will adjust accordingly.
          </p>
        )}
        
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label className="form-label text-white">Parameter to Optimize</label>
            <select 
              className="form-select bg-dark text-light border-secondary"
              value={selectedParam}
              onChange={(e) => {
                setSelectedParam(e.target.value);
                setResult(null);
              }}
              disabled={isCalculating}
            >
              <option value={GOAL_SEEK_PARAMS.PRICE}>Purchase Price</option>
              <option value={GOAL_SEEK_PARAMS.DOWN_PAYMENT}>Down Payment %</option>
              <option value={GOAL_SEEK_PARAMS.INTEREST_RATE}>Interest Rate %</option>
              <option value={GOAL_SEEK_PARAMS.CLOSING_COSTS}>Closing Costs</option>
              <option value={GOAL_SEEK_PARAMS.MONTHLY_RENT}>Monthly Rent</option>
            </select>
            <small className="text-muted">
              Current value: {selectedParam === GOAL_SEEK_PARAMS.PRICE ? '$' + Number(scenarioData.price || 0).toLocaleString() : 
                selectedParam === GOAL_SEEK_PARAMS.DOWN_PAYMENT ? (Number(scenarioData.downPaymentPercent || 0)).toFixed(2) + '%' :
                selectedParam === GOAL_SEEK_PARAMS.INTEREST_RATE ? (Number(scenarioData.interestRate || 0)).toFixed(2) + '%' :
                selectedParam === GOAL_SEEK_PARAMS.MONTHLY_RENT ? '$' + Number(scenarioData.monthlyRent || 0).toLocaleString() :
                '$' + Number(scenarioData.closingCosts || 0).toLocaleString()}
            </small>
          </div>
          
          <div className="col-md-6">
            <label className="form-label text-white">Metric to Optimize For</label>
            <select 
              className="form-select bg-dark text-light border-secondary"
              value={selectedMetric}
              onChange={(e) => {
                setSelectedMetric(e.target.value);
                setResult(null);
              }}
              disabled={isCalculating}
            >
              <option value={GOAL_SEEK_METRICS.TOTAL_ROI}>Total ROI</option>
              <option value={GOAL_SEEK_METRICS.CASH_ON_CASH}>Cash on Cash Return</option>
              <option value={GOAL_SEEK_METRICS.CAP_RATE}>Cap Rate</option>
            </select>
            <small className="text-muted">
              Current value: {
                selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 
                  `${scenarioData.calculatedROI?.toFixed(2) || 0}%` : 
                selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 
                  `${scenarioData.calculations?.annualCashOnCashReturn?.toFixed(2) || 0}%` : 
                  `${scenarioData.calculations?.capRate?.toFixed(2) || 0}%`
              }
            </small>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="form-label text-white">
            Target {selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 'Total ROI' : 
                   selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 'Cash on Cash Return' : 
                   'Cap Rate'}
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control bg-dark text-light border-secondary"
              value={targetValue}
              onChange={handleTargetValueChange}
              placeholder={`Enter target ${selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 'ROI' : 
                                                selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 'Cash on Cash' : 
                                                'Cap Rate'}`}
              disabled={isCalculating}
            />
            <span className="input-group-text">%</span>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-4">
            <h6 className="text-white mb-3">Goal Seek Results</h6>
              
              <div className="mb-3">
                <label className="form-label text-white">
                  {result.paramName === 'price' ? 'Optimal Purchase Price' :
                   result.paramName === 'downPaymentPercent' ? 'Optimal Down Payment' :
                   result.paramName === 'interestRate' ? 'Optimal Interest Rate' :
                   result.paramName === 'monthlyRent' ? 'Optimal Monthly Rent' :
                   'Optimal Closing Costs'}
                </label>
                <div className="input-group">
                  {(result.paramUnit === '$') && <span className="input-group-text">$</span>}
                  <input
                    type="text"
                    className="form-control bg-dark text-light border-secondary"
                    value={result.paramUnit === '$' ? 
                      Number(result.optimalValue).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : 
                      result.optimalValue.toFixed(2)}
                    readOnly
                  />
                  {(result.paramUnit === '%') && <span className="input-group-text">%</span>}
                </div>
                <small className="text-muted">
                  Current value: {result.paramName === 'price' ? '$' + Number(scenarioData.price || 0).toLocaleString() :
                   result.paramName === 'downPaymentPercent' ? (Number(scenarioData.downPaymentPercent || 0)).toFixed(2) + '%' :
                   result.paramName === 'interestRate' ? (Number(scenarioData.interestRate || 0)).toFixed(2) + '%' :
                   result.paramName === 'monthlyRent' ? '$' + Number(scenarioData.monthlyRent || 0).toLocaleString() :
                   '$' + Number(scenarioData.closingCosts || 0).toLocaleString()}
                </small>
              </div>
              
                      <div className="mb-3">
                <label className="form-label text-white">
                  {selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 'Total ROI Analysis' : 
                   selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 'Cash on Cash Analysis' : 
                   'Cap Rate Analysis'}
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control bg-dark text-light border-secondary"
                    value={result.calculatedROI.toFixed(2)}
                    readOnly
                  />
                  <span className="input-group-text">%</span>
                </div>
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-muted">
                    Target {selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? 'ROI' : 
                           selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? 'Cash on Cash' : 
                           'Cap Rate'}: {targetValue}%
                  </small>
                  <small className={Math.abs(result.calculatedROI - parseFloat(targetValue)) < 0.1 ? "text-success" : "text-warning"}>
                    {Math.abs(result.calculatedROI - parseFloat(targetValue)) < 0.1 ? 
                      <><i className="bi bi-check-circle me-1"></i>Exact match</> : 
                      <><i className="bi bi-info-circle me-1"></i>Closest possible value</>}
                  </small>
                </div>
              </div>
              
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                This is the {Math.abs(result.calculatedROI - parseFloat(targetValue)) < 0.1 ? "exact" : "closest possible"} {result.paramName === 'price' ? 'purchase price' :
                   result.paramName === 'downPaymentPercent' ? 'down payment percentage' :
                   result.paramName === 'interestRate' ? 'interest rate' :
                   result.paramName === 'monthlyRent' ? 'monthly rent' :
                   'closing costs'} that will achieve {Math.abs(result.calculatedROI - parseFloat(targetValue)) < 0.1 ? "exactly" : "approximately"} {targetValue}% 
                {selectedMetric === GOAL_SEEK_METRICS.TOTAL_ROI ? ' Total ROI' : 
                 selectedMetric === GOAL_SEEK_METRICS.CASH_ON_CASH ? ' Cash on Cash Return' : 
                 ' Cap Rate'}.
              </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

GoalSeekModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  scenarioData: PropTypes.object.isRequired,
  onApplyPrice: PropTypes.func.isRequired
};

export default GoalSeekModal;
